import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

interface Report {
  id: number
  report_id: string
  patient: number
  patient_name: string
  patient_code: string
  status: string
  diagnosis: string
  pathologist: number | null
  pathologist_name: string | null
  created_at: string
  finalized_at: string | null
  exported_pdf: boolean
  exported_docx: boolean
  report_type?: 'histopathology' | 'cytology'
  conclusion?: string
  cytopathologist?: string
}

const ReportManagement: React.FC = () => {
  const { t } = useTranslation()
  const [reports, setReports] = useState<Report[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null)

  const statuses = ['DRAFT', 'PENDING_REVIEW', 'REVIEWED', 'FINALIZED', 'AMENDED']

  const fetchReports = async () => {
    try {
      setIsLoading(true)

      // Fetch both histopathology and cytology reports
      const [histoResponse, cytoResponse] = await Promise.all([
        fetch('/api/v1/pathology/reports/'),
        fetch('/api/v1/pathology/cytology-reports/')
      ])

      let allReports: Report[] = []

      // Process histopathology reports
      if (histoResponse.ok) {
        const histoData = await histoResponse.json()
        const histoReports = (histoData.results || histoData).map((r: any) => ({
          ...r,
          report_type: 'histopathology' as const,
          report_id: r.report_id || `HISTO-${r.id}`
        }))
        allReports = [...allReports, ...histoReports]
      }

      // Process cytology reports
      if (cytoResponse.ok) {
        const cytoData = await cytoResponse.json()
        const cytoReports = (cytoData.results || cytoData).map((r: any) => ({
          ...r,
          report_type: 'cytology' as const,
          report_id: `CYT-${r.id}`,
          diagnosis: r.conclusion || r.diagnosis || '',
          pathologist_name: r.cytopathologist || null,
          created_at: r.report_date || r.created_at
        }))
        allReports = [...allReports, ...cytoReports]
      }

      // Sort by created date (newest first)
      allReports.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

      setReports(allReports)
    } catch (error) {
      console.error('Error fetching reports:', error)
      toast.error('Error fetching reports')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchReports()
  }, [])

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'bg-gray-100 text-gray-800',
      PENDING_REVIEW: 'bg-yellow-100 text-yellow-800',
      REVIEWED: 'bg-blue-100 text-blue-800',
      FINALIZED: 'bg-green-100 text-green-800',
      AMENDED: 'bg-purple-100 text-purple-800',
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const filteredReports = reports.filter(report => {
    const matchesSearch =
      report.report_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.diagnosis.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || report.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const handleExportPDF = async (reportId: number) => {
    try {
      const response = await fetch(`/api/v1/pathology/reports/${reportId}/export_pdf/`)
      if (response.ok) {
        const data = await response.json()
        toast.success(data.message || 'PDF export initiated')
      } else {
        toast.error('Failed to export PDF')
      }
    } catch (error) {
      console.error('Error exporting PDF:', error)
      toast.error('Error exporting PDF')
    }
  }

  const handleExportDOCX = async (reportId: number) => {
    try {
      const response = await fetch(`/api/v1/pathology/reports/${reportId}/export_docx/`)
      if (response.ok) {
        const data = await response.json()
        toast.success(data.message || 'DOCX export initiated')
      } else {
        toast.error('Failed to export DOCX')
      }
    } catch (error) {
      console.error('Error exporting DOCX:', error)
      toast.error('Error exporting DOCX')
    }
  }

  const openDeleteModal = (report: Report) => {
    setReportToDelete(report)
    setShowDeleteModal(true)
  }

  const handleDeleteReport = async () => {
    if (!reportToDelete) return

    try {
      // Use correct endpoint based on report type
      const endpoint = reportToDelete.report_type === 'cytology'
        ? `/api/v1/pathology/cytology-reports/${reportToDelete.id}/`
        : `/api/v1/pathology/reports/${reportToDelete.id}/`

      const response = await fetch(endpoint, {
        method: 'DELETE'
      })

      if (response.ok) {
        toast.success('Đã xóa báo cáo')
        setShowDeleteModal(false)
        setReportToDelete(null)
        fetchReports()
      } else {
        const errorData = await response.json()
        toast.error(`Lỗi: ${errorData.detail || 'Không thể xóa báo cáo'}`)
      }
    } catch (error) {
      console.error('Error deleting report:', error)
      toast.error('Lỗi xóa báo cáo')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('report.reportManagement')}</h1>
          <p className="text-gray-600">{t('report.manageReports')}</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/pathology/reports/cytology"
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center"
          >
            <span className="mr-2">📋</span>
            {t('cytology.title')}
          </Link>
          <Link
            to="/pathology/reports/create"
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <span className="mr-2">+</span>
            {t('report.newReport')}
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.search')}</label>
            <input
              type="text"
              placeholder={t('report.searchReports')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('report.status')}</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">{t('report.allStatus')}</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loại
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('report.reportId')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('report.patient')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('report.diagnosis')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('report.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('report.date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('report.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    Loading reports...
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No reports found. Create your first report to get started.
                  </td>
                </tr>
              ) : (
                filteredReports.map((report) => (
                  <tr key={`${report.report_type}-${report.id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        report.report_type === 'cytology'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {report.report_type === 'cytology' ? '🔬 Tế bào' : '🧬 Mô'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {report.report_id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {report.patient_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {report.patient_code}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {report.diagnosis || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(report.status)}`}>
                        {report.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(report.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Link
                          to={report.report_type === 'cytology'
                            ? `/pathology/cytology-reports/${report.id}`
                            : `/pathology/reports/${report.id}/edit`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {t('common.view')}
                        </Link>
                        {report.report_type === 'histopathology' && (
                          <>
                            <button
                              onClick={() => handleExportPDF(report.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              {t('report.pdf')}
                            </button>
                            <button
                              onClick={() => handleExportDOCX(report.id)}
                              className="text-purple-600 hover:text-purple-900"
                            >
                              {t('report.docx')}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => openDeleteModal(report)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Xóa
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && reportToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-96 shadow-lg rounded-md bg-white">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Xác nhận xóa báo cáo</h3>
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-2">
                  Bạn có chắc chắn muốn xóa báo cáo này?
                </p>
                <div className="bg-gray-50 p-3 rounded text-left">
                  <p className="text-sm font-medium text-gray-900">{reportToDelete.report_id}</p>
                  <p className="text-sm text-gray-600">{reportToDelete.patient_name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Trạng thái: <span className="font-medium">{reportToDelete.status}</span>
                  </p>
                </div>
                <p className="text-sm text-red-600 mt-3 font-medium">
                  ⚠️ Hành động này không thể hoàn tác!
                </p>
              </div>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setReportToDelete(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeleteReport}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
                >
                  Xóa báo cáo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportManagement
